/**
 * Sentry Configuration for PetSnap Frontend
 * Production error tracking and performance monitoring
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only runs in production to avoid noise in development
 */
export const initializeSentry = () => {
    // Only initialize in production
    if (process.env.NODE_ENV !== 'production') {
        console.log('📊 Sentry disabled in development mode');
        return;
    }

    const dsn = process.env.REACT_APP_SENTRY_DSN;

    if (!dsn) {
        console.warn('⚠️ REACT_APP_SENTRY_DSN not configured - Sentry disabled');
        return;
    }

    Sentry.init({
        dsn,
        environment: process.env.NODE_ENV || 'production',

        // Performance Monitoring
        tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring

        // Release tracking for better debugging
        release: process.env.REACT_APP_VERSION || '0.1.0',

        // Ignore known benign errors
        ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            'chrome-extension://',
            'moz-extension://',

            // Common React errors that aren't actionable
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',

            // Network errors (handled by app)
            'NetworkError',
            'Network request failed',

            // Non-Error promise rejections
            'Non-Error promise rejection captured',
        ],

        // Filter before sending to Sentry
        beforeSend(event, hint) {
            // Don't send in development
            if (process.env.NODE_ENV !== 'production') {
                return null;
            }

            // Filter out errors from localhost API calls (development backend)
            if (event.request?.url?.includes('localhost:3001')) {
                return null;
            }

            return event;
        },
    });

    // Set initial context
    Sentry.setTag('app', 'petsnap-frontend');
    Sentry.setContext('build', {
        version: process.env.REACT_APP_VERSION || '0.1.0',
        buildTime: new Date().toISOString(),
    });

    console.log('✅ Sentry initialized successfully');
};

/**
 * Track custom events with Sentry breadcrumbs
 */
export const trackEvent = (category: string, message: string, data?: any) => {
    Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
        timestamp: Date.now() / 1000,
    });
};

/**
 * Track errors with additional context
 */
export const trackError = (
    error: Error,
    context?: {
        tags?: Record<string, string>;
        extra?: Record<string, any>;
    }
) => {
    Sentry.captureException(error, {
        tags: context?.tags || {},
        extra: context?.extra || {},
    });
};

/**
 * Set user context for better error tracking
 */
export const setUserContext = (user: {
    id: string;
    email?: string;
    username?: string;
}) => {
    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
    });
};

/**
 * Clear user context (on logout)
 */
export const clearUserContext = () => {
    Sentry.setUser(null);
};

export default Sentry;
